import { Link } from 'react-router-dom';
import { Moon, Sun, Eye, LogOut, User, Search, Users } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import { logout } from '@/api/auth';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { AvatarCircle } from '@/components/members/AvatarCircle';

interface HeaderProps {
  memberCount: number;
  countryCount: number;
  companyCount: number;
}

export function Header({ memberCount, countryCount, companyCount }: HeaderProps) {
  const { role, email, phone, getEffectiveRole, toggleViewRole } = useAuthStore();
  const { theme, toggleTheme, setCommandOpen } = useUIStore();
  const effectiveRole = getEffectiveRole();
  const displayName = email || phone || 'User';

  return (
    <header className="sticky top-0 z-30 glass border-b border-border">
      <div className="flex items-center justify-between h-14 px-4 md:px-6">
        {/* Left: Logo + title */}
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 shrink-0">
            <Users className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0">
            <h1 className="text-base font-bold text-foreground leading-tight truncate">Community</h1>
            <p className="text-[10px] text-muted-foreground leading-tight hidden md:block">
              {memberCount > 0
                ? `${memberCount.toLocaleString()} members · ${countryCount} countries · ${companyCount.toLocaleString()} companies`
                : 'Loading...'}
            </p>
          </div>
        </div>

        {/* Right: Search + Avatar dropdown */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCommandOpen(true)}
            title="Search (Ctrl+K)"
            aria-label="Search"
            className="h-8 w-8"
          >
            <Search className="h-4 w-4" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="rounded-full ring-offset-background transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                <AvatarCircle name={displayName} size="sm" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel className="font-normal">
                <div className="text-sm font-medium truncate">{displayName}</div>
                {role === 'admin' && (
                  <div className="text-xs text-muted-foreground">Admin</div>
                )}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/profile" className="cursor-pointer">
                  <User className="h-4 w-4" />
                  My Profile
                </Link>
              </DropdownMenuItem>
              {role === 'admin' && (
                <DropdownMenuItem onClick={toggleViewRole} className="cursor-pointer">
                  <Eye className="h-4 w-4" />
                  {effectiveRole === 'admin' ? 'View as User' : 'View as Admin'}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={toggleTheme} className="cursor-pointer">
                {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="cursor-pointer text-destructive focus:text-destructive">
                <LogOut className="h-4 w-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
